import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const currency = (n) => `$${n.toFixed(2)}`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const resolveSrc = (u) => (typeof u === "string" && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

const isPdf = (u) => typeof u === "string" && /\.pdf(\?|#|$)/i.test(u);

// Normalize any YouTube/Vimeo/direct-MP4 URL. Returns {kind: 'iframe'|'video', src}.
const toPlayable = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const s = raw.trim();
  let u;
  try { u = new URL(s); } catch { return null; }
  const host = u.hostname.toLowerCase();
  const pathLower = u.pathname.toLowerCase();
  if (/\.(mp4|m4v|webm|ogg|ogv|mov)(\?|#|$)/.test(pathLower)) {
    return { kind: "video", src: s };
  }
  if (host === "youtu.be" || host === "www.youtu.be") {
    const id = u.pathname.replace(/^\//, "").split("/")[0];
    return id ? { kind: "iframe", src: `https://www.youtube.com/embed/${id}` } : null;
  }
  if (host.endsWith("youtube.com")) {
    if (u.pathname === "/watch") {
      const id = u.searchParams.get("v");
      return id ? { kind: "iframe", src: `https://www.youtube.com/embed/${id}` } : null;
    }
    if (u.pathname.startsWith("/embed/")) return { kind: "iframe", src: s };
    if (u.pathname.startsWith("/shorts/")) {
      const id = u.pathname.split("/shorts/")[1].split("/")[0];
      return id ? { kind: "iframe", src: `https://www.youtube.com/embed/${id}` } : null;
    }
  }
  if (host === "vimeo.com" || host === "www.vimeo.com" || host === "player.vimeo.com") {
    if (u.pathname.startsWith("/video/")) return { kind: "iframe", src: s };
    const parts = u.pathname.split("/").filter(Boolean);
    for (let i = parts.length - 1; i >= 0; i--) {
      if (/^\d+$/.test(parts[i])) return { kind: "iframe", src: `https://player.vimeo.com/video/${parts[i]}` };
    }
  }
  return null;
};

export const VideoEmbed = ({ url, title }) => {
  const p = toPlayable(url);
  if (!p) return null;
  return (
    <div
      data-testid="course-video-embed"
      className="w-full aspect-video bg-black rounded-sm overflow-hidden mb-6 border border-[#E8DCDD]"
    >
      {p.kind === "video" ? (
        <video
          src={p.src}
          controls
          preload="metadata"
          controlsList="nodownload"
          className="w-full h-full"
          data-testid="course-video-mp4"
        />
      ) : (
        <iframe
          src={p.src}
          title={`${title} — course video`}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full border-0"
        />
      )}
    </div>
  );
};

const CoverImage = ({ src, title, className }) => {
  const resolved = resolveSrc(src);
  if (isPdf(resolved)) {
    return (
      <div className={`${className} pointer-events-none bg-white`}>
        <iframe src={`${resolved}#toolbar=0&navpanes=0&view=FitH`} title={title}
          className="w-full h-full border-0" />
      </div>
    );
  }
  return <img src={resolved} alt={title} className={className} />;
};

const ProductCard = ({ product, onOpen }) => (
  <div
    data-testid={`product-card-${product.id}`}
    className="bg-white border border-[#E8DCDD] rounded-md flex flex-col overflow-hidden transition-all duration-200 hover:border-[#8C4A57] hover:shadow-sm"
  >
    <div className="w-full aspect-[4/5] bg-[#FAF4F5] overflow-hidden relative">
      <CoverImage src={product.cover} title={product.title}
        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
      {product.new_edition && (
        <div
          data-testid={`new-edition-ribbon-${product.id}`}
          className="absolute top-3 left-3 bg-[#8C4A57] text-white text-[10px] font-semibold uppercase tracking-[0.18em] px-2.5 py-1 rounded-sm shadow-md"
        >
          New Edition
        </div>
      )}
    </div>
    <div className="p-5 flex flex-col flex-1">
      <div className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62] mb-2">{product.category_label}</div>
      <h3 className="text-lg font-serif font-semibold text-[#2D2628] leading-tight line-clamp-2 mb-2">{product.title}</h3>
      <div className="flex items-baseline gap-2 mb-4">
        <span className="text-[#8C4A57] font-semibold text-base">{currency(product.price)}</span>
        {product.regular_price > product.price && (
          <span className="text-xs text-[#6B5E62] line-through">{currency(product.regular_price)}</span>
        )}
      </div>
      {product.type === "course" ? (
        <Link
          to={`/courses/${product.id}`}
          data-testid={`product-read-more-${product.id}`}
          className="mt-auto w-full border border-[#8C4A57] text-[#8C4A57] hover:bg-[#8C4A57] hover:text-white py-2 rounded-sm text-xs font-semibold uppercase tracking-wide transition-colors duration-200 text-center inline-block"
        >
          Read More
        </Link>
      ) : (
        <button
          onClick={() => onOpen(product)}
          data-testid={`product-read-more-${product.id}`}
          className="mt-auto w-full border border-[#8C4A57] text-[#8C4A57] hover:bg-[#8C4A57] hover:text-white py-2 rounded-sm text-xs font-semibold uppercase tracking-wide transition-colors duration-200"
        >
          Read More
        </button>
      )}
    </div>
  </div>
);

export const ProductModal = ({ product, open, onOpenChange }) => {
  const { addItem } = useCart();
  const [previewOpen, setPreviewOpen] = useState(false);
  if (!product) return null;
  const handleAdd = () => {
    addItem(product);
    toast.success(`${product.title} added to cart`);
    onOpenChange(false);
  };
  const previewItems = Array.isArray(product.preview_items) ? product.preview_items : [];
  const hasPreview = previewItems.length > 0;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setPreviewOpen(false); onOpenChange(v); }}>
      <DialogContent data-testid="product-modal" className="max-w-3xl p-0 overflow-hidden border-[#E8DCDD] max-h-[90vh] overflow-y-auto">
        {product.type === "course" && (
          <div className="p-6 pb-0" data-testid="course-locked-teaser">
            <div className="w-full bg-[#FAF4F5] border border-[#E8DCDD] rounded-sm px-5 py-4 flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white border border-[#E8DCDD] flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                     fill="none" stroke="#8C4A57" strokeWidth="1.8" strokeLinecap="round"
                     strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-1">
                  Included with this course
                </div>
                <p className="text-sm text-[#4A3E42] leading-relaxed">
                  Course video, downloadable workbook, reading materials, and free book are included
                  — available after purchase.
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="bg-[#FAF4F5] p-8 flex items-center justify-center min-h-[280px]">
            <CoverImage src={product.cover} title={product.title}
              className="max-h-[360px] h-[360px] w-[280px] shadow-md rounded-sm object-cover bg-white" />
          </div>
          <div className="p-8 flex flex-col">
            <div className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62] mb-2">
              {product.category_label} · {product.type === "ebook" ? "Ebook" : "Course"}
            </div>
            <DialogTitle asChild>
              <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-[#2D2628] leading-tight mb-3">
                {product.title}
              </h2>
            </DialogTitle>
            <DialogDescription className="sr-only">
              {product.short_desc || product.description || product.title}
            </DialogDescription>
            <div className="flex items-baseline gap-3 mb-5">
              <span className="text-2xl text-[#8C4A57] font-semibold">{currency(product.price)}</span>
              {product.regular_price > product.price && (
                <span className="text-sm text-[#6B5E62] line-through">{currency(product.regular_price)}</span>
              )}
            </div>
            <p className="text-sm text-[#6B5E62] leading-relaxed mb-5">{product.description}</p>
            <div className="text-xs text-[#6B5E62] mb-6">
              <div><span className="font-semibold text-[#2D2628]">Format:</span> {product.format}</div>
              <div className="mt-1"><span className="font-semibold text-[#2D2628]">Instructor:</span> Dr. Andrew Campbell</div>
            </div>
            <button
              onClick={handleAdd}
              data-testid={`add-to-cart-${product.id}`}
              className="w-full bg-[#8C4A57] text-white hover:bg-[#703B45] py-3 rounded-sm text-sm font-semibold uppercase tracking-wide transition-colors duration-200 shadow-sm"
            >
              Add to Cart
            </button>
            {hasPreview && (
              <button
                onClick={() => setPreviewOpen((v) => !v)}
                data-testid={`bundle-preview-toggle-${product.id}`}
                className="mt-3 w-full border border-[#8C4A57] text-[#8C4A57] hover:bg-[#FAF4F5] py-3 rounded-sm text-sm font-semibold uppercase tracking-wide transition-colors duration-200"
              >
                {previewOpen ? "Hide Preview" : "Preview Inside →"}
              </button>
            )}
          </div>
        </div>
        {hasPreview && previewOpen && (
          <div
            data-testid={`bundle-preview-panel-${product.id}`}
            className="border-t border-[#E8DCDD] bg-[#FAF4F5] p-6 sm:p-8"
          >
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-4">
              What's inside the bundle
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {previewItems.map((item, idx) => (
                <div
                  key={idx}
                  data-testid={`bundle-preview-item-${product.id}-${idx}`}
                  className="bg-white border border-[#E8DCDD] rounded-sm p-4 flex gap-4"
                >
                  <div className="w-24 h-32 flex-shrink-0 bg-[#FAF4F5] rounded-sm overflow-hidden">
                    <CoverImage src={item.cover} title={item.title}
                      className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-serif font-semibold text-[#2D2628] leading-snug mb-2">
                      {item.title}
                    </h4>
                    <p className="text-xs text-[#6B5E62] leading-relaxed">{item.blurb}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProductCard;
